import { useState } from "react";

import { Check, Copy, Key, Loader2, Plus, Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LicenseRequestsQueue } from "@/features/licenses/components/LicenseRequestsQueue";
import {
  useDeleteLicense,
  useGenerateLicense,
  useLicenseRequests,
  useLicensesByUser,
} from "@/features/licenses/hooks/useLicenses";
import {
  softwareTypes,
  type SoftwareType,
} from "@/features/licenses/schemas/license";
import { softwareTypeLabel } from "@/features/servers/schemas/servers";
import { cn } from "@/shared/lib/utils";

const TAB_TRIGGER_CLASS = cn(
  "cursor-pointer gap-1.5 rounded-none border-0 border-b-2 border-transparent px-3 py-2.5",
  "bg-transparent shadow-none text-muted-foreground",
  "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
  "dark:data-[state=active]:bg-transparent dark:data-[state=active]:border-b-primary",
  "data-[state=active]:border-b-primary data-[state=active]:text-foreground"
);

interface LicensesDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onClose: () => void;
}

export function LicensesDialog({
  userId,
  userName,
  open,
  onClose,
}: LicensesDialogProps) {
  const { data: licenses, isLoading } = useLicensesByUser(userId);
  const { count: pendingCount } = useLicenseRequests({
    status: "pending",
    userId,
    enabled: open && !!userId,
  });
  const generateLicense = useGenerateLicense();
  const deleteLicense = useDeleteLicense();

  const [tab, setTab] = useState<"requests" | "licenses">("requests");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newMachineId, setNewMachineId] = useState("");
  const [newClientName, setNewClientName] = useState("");
  const [newSoftwareType, setNewSoftwareType] = useState<string>("");
  const [newlyCreatedLicense, setNewlyCreatedLicense] = useState<{
    signature: string;
    machineId: string;
  } | null>(null);
  const [copiedSignature, setCopiedSignature] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newMachineId.trim() || !newClientName.trim() || !newSoftwareType) return;

    try {
      await generateLicense.mutateAsync({
        machineId: newMachineId,
        clientName: newClientName,
        softwareType: newSoftwareType as SoftwareType,
        userId,
      });

      setNewlyCreatedLicense({
        signature: "Generated successfully",
        machineId: newMachineId,
      });
      setShowCreateForm(false);
      setNewMachineId("");
      setNewClientName("");
      setNewSoftwareType("");
      setTab("licenses");
    } catch (error) {
      console.error("Error generating license:", error);
    }
  };

  const handleDelete = async (licenseId: string) => {
    try {
      await deleteLicense.mutateAsync(licenseId);
    } catch (error) {
      console.error("Error deleting license:", error);
    }
  };

  const copyToClipboard = (value: string) => {
    void navigator.clipboard.writeText(value);
    setCopiedSignature(value);
    setTimeout(() => setCopiedSignature(null), 2000);
  };

  const handleClose = () => {
    setShowCreateForm(false);
    setNewMachineId("");
    setNewClientName("");
    setNewSoftwareType("");
    setNewlyCreatedLicense(null);
    setTab("requests");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Key className="h-6 w-6 text-primary" />
            Licencias
          </DialogTitle>
          <DialogDescription>
            Solicitudes y licencias de {userName}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "requests" | "licenses")}
          className="w-full gap-4 pt-2"
        >
          <div className="border-b border-border">
            <TabsList
              className="h-auto w-full justify-start gap-0 rounded-none bg-transparent p-0"
              aria-label="Secciones de licencias"
            >
              <TabsTrigger value="requests" className={TAB_TRIGGER_CLASS}>
                Solicitudes
                {pendingCount > 0 && (
                  <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-white">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="licenses" className={TAB_TRIGGER_CLASS}>
                Licencias
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="requests" className="mt-0 outline-none">
            <LicenseRequestsQueue userId={userId} hideHeader />
          </TabsContent>

          <TabsContent value="licenses" className="mt-0 space-y-4 outline-none">
            {newlyCreatedLicense && (
              <div className="bg-green-50 border border-green-200 dark:bg-green-950/30 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="font-medium text-green-800 dark:text-green-200">
                      Licencia creada exitosamente
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Machine ID: {newlyCreatedLicense.machineId}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="cursor-pointer"
                    onClick={() =>
                      copyToClipboard(newlyCreatedLicense.signature)
                    }
                  >
                    {copiedSignature === newlyCreatedLicense.signature ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  La firma digital ha sido generada y guardada exitosamente.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 cursor-pointer"
                  onClick={() => setNewlyCreatedLicense(null)}
                >
                  Entendido
                </Button>
              </div>
            )}

            {showCreateForm && (
              <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Nueva Licencia</Label>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="cursor-pointer"
                    onClick={() => setShowCreateForm(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="machineId">Machine ID</Label>
                  <Input
                    id="machineId"
                    value={newMachineId}
                    onChange={(e) => setNewMachineId(e.target.value)}
                    placeholder="Ej: machine-001"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientName">Nombre del Cliente</Label>
                  <Input
                    id="clientName"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    placeholder="Ej: Hospital Central"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="softwareType">Tipo de Software</Label>
                  <Select
                    value={newSoftwareType}
                    onValueChange={setNewSoftwareType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo de software" />
                    </SelectTrigger>
                    <SelectContent>
                      {softwareTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {softwareTypeLabel(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  className="cursor-pointer"
                  onClick={() => void handleCreate()}
                  disabled={
                    generateLicense.isPending ||
                    !newMachineId.trim() ||
                    !newClientName.trim() ||
                    !newSoftwareType
                  }
                >
                  {generateLicense.isPending && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Generar Licencia
                </Button>
              </div>
            )}

            {!showCreateForm && !isLoading && (
              <Button
                className="cursor-pointer"
                onClick={() => setShowCreateForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nueva Licencia
              </Button>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : licenses && licenses.length > 0 ? (
              <div className="space-y-2">
                {licenses.map((license) => (
                  <div
                    key={license.licenseId}
                    className="border rounded-lg p-4 space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          {softwareTypeLabel(license.softwareType)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Cliente: {license.clientName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Machine ID: {license.machineId}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Creada:{" "}
                          {new Date(license.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                            license.status === "active"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200"
                              : license.status === "revoked"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200"
                                : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
                          }`}
                        >
                          {license.status}
                        </span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="cursor-pointer"
                          title="Copiar Firma"
                          onClick={() => copyToClipboard(license.signature)}
                        >
                          {copiedSignature === license.signature ? (
                            <Check className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="cursor-pointer"
                          title="Eliminar"
                          onClick={() => void handleDelete(license.licenseId)}
                          disabled={deleteLicense.isPending}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : !showCreateForm ? (
              <p className="text-center text-muted-foreground py-8">
                No hay licencias para este usuario
              </p>
            ) : null}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" className="cursor-pointer" onClick={handleClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
